// 후처리 체인(부팅 때 그래프를 한 번만 연결하고 이후엔 uniform만 바꾼다. 그래프 구조 변경 = 전체 재컴파일).
// 장면 → 크로마틱 수차 → 블룸 → renderOutput(톤매핑·색공간) → 틴트 → 비네트 → 집중선 → 섬광.
// 섬광·틴트를 톤매핑 뒤에 합성해야 순백이 실제로 나온다(outputColorTransform=false).
import * as THREE from 'three/webgpu';
import { atan, float, hash, length, min, mix, pass, renderOutput, screenUV, smoothstep, step, uniform, vec2, vec3, vec4 } from 'three/tsl';
import { bloom } from 'three/addons/tsl/display/BloomNode.js';
import type { QualitySpec } from './quality';
import type { MapMood } from './env/mood';
import { kaelDepthStencil } from './kaelOutline';

const _c = new THREE.Color();

export class PostFx {
  readonly pipeline: THREE.RenderPipeline;
  readonly tintColor = uniform(new THREE.Color(1.0, 0.3, 0.34));
  readonly tint = uniform(0);
  readonly vignette = uniform(0);
  readonly chroma = uniform(0);
  readonly lines = uniform(0);
  /** 결투장식 KO: 화면을 어둡고 붉게 누르는 세기(0..1)와 집중선 세기. KoScreen(src/vfx/ko.ts)이 실제 시간으로 바꾼다 */
  readonly ko = uniform(0);
  readonly koLines = uniform(0);
  readonly flashColor = uniform(new THREE.Color(1, 1, 1));
  readonly flash = uniform(0);
  /** 맵 분위기 그레이드: 햇무리 색·세기·중심, 채도 */
  readonly glowColor = uniform(new THREE.Color(1, 0.7, 0.4));
  readonly glow = uniform(0);
  readonly glowAt = uniform(new THREE.Vector2(0.1, 0));
  readonly saturation = uniform(1);
  /** 분할 톤: 그늘 색·햇빛 색·세기·검정 들어올림 */
  readonly shadowTint = uniform(new THREE.Vector3(1, 1, 1));
  readonly highTint = uniform(new THREE.Vector3(1, 1, 1));
  readonly split = uniform(0);
  readonly lift = uniform(0);
  /**
   * 블룸 입력 HDR 상한. 덧셈 VFX가 겹치면 HDR 값이 10 이상까지 쌓이고, 블룸이 그것을 넓게 퍼뜨려 톤매핑 뒤 분홍·흰 막이 됐다
   * (각성 난무 프레임 분리 캡처: 블룸 끔 9.7% vs 켬 22.7% 분홍 화소, 임계 0.9→1.8은 22.7→19.6%만 줄었다). 상한으로 겹침이 블룸을 키우지 못하게 한다.
   */
  readonly bloomCap = uniform(1.5);
  /** 맵 분위기 블룸 세기(setMood)와 연출 배율(setBloomScale: 각성 난무 중 낮춘다) */
  private bloomBase = 0.6;
  private bloomMul = 1;
  private readonly bloomNode: ReturnType<typeof bloom>;
  private readonly scenePass: ReturnType<typeof pass>;
  private samples: number;
  private bloomScale: number;

  constructor(renderer: THREE.WebGPURenderer, scene: THREE.Scene, camera: THREE.Camera, q: QualitySpec) {
    this.samples = q.msaa ? 4 : 0;
    this.bloomScale = q.bloomScale;
    const scenePass = pass(scene, camera, {
      samples: this.samples, stencilBuffer: true, depthTexture: kaelDepthStencil(),
    });
    this.scenePass = scenePass;
    const tex = scenePass.getTextureNode('output');
    const uv0 = screenUV;
    const dir = uv0.sub(0.5);
    const off = dir.mul(this.chroma);
    const base = vec3(tex.sample(uv0.add(off)).r, tex.sample(uv0).g, tex.sample(uv0.sub(off)).b);

    this.bloomNode = bloom(vec4(min(tex.rgb, vec3(this.bloomCap)), 1), 0.6, 0.35, 0.95);
    this.bloomNode.setResolutionScale(0.5 * q.bloomScale);
    const ldr = renderOutput(vec4(base, 1).add(this.bloomNode)).rgb;

    // 분위기 그레이드(톤매핑 뒤): 채도 → 해 쪽 모서리 햇무리(가산, 거리 제곱 감쇠). 섬광·틴트보다 먼저 둔다
    const lum = ldr.dot(vec3(0.2126, 0.7152, 0.0722));
    const graded = mix(vec3(lum), ldr, this.saturation);
    const gd = length(uv0.sub(this.glowAt).mul(vec2(1.25, 1.0)));
    const gfall = smoothstep(1.05, 0.0, gd);
    let sunlit = graded.add(this.glowColor.mul(gfall.mul(gfall)).mul(this.glow));
    // 분할 톤: 밝기에 따라 그늘 색 → 햇빛 색을 곱하고(세기 split), 가장 어두운 곳은 그늘 색 쪽으로 조금 들어 올린다
    const lum2 = sunlit.dot(vec3(0.2126, 0.7152, 0.0722));
    // 색은 밝기로 나눠 색조만 옮긴다(밝기는 그대로)
    const st = this.shadowTint, ht = this.highTint;
    const W = vec3(0.2126, 0.7152, 0.0722);
    const toneMul = mix(st.div(st.dot(W).max(0.05)), ht.div(ht.dot(W).max(0.05)), smoothstep(0.08, 0.65, lum2));
    sunlit = mix(sunlit, sunlit.mul(toneMul), this.split);
    sunlit = sunlit.mul(float(1).sub(this.lift)).add(this.shadowTint.mul(this.lift));

    // 틴트(붉은 각성 톤): 밝기를 낮추고(검붉게) 붉은색으로 곱한다. 더하기는 아주 조금만(분홍 안개가 되지 않게)
    let c = mix(sunlit, sunlit.mul(this.tintColor).mul(0.82).add(this.tintColor.mul(0.02)), this.tint);
    // 비네트(짙은 적흑)
    const rad = length(dir.mul(vec2(1.5, 1.0)));
    c = mix(c, vec3(0.16, 0.0, 0.02), smoothstep(0.32, 0.9, rad).mul(this.vignette));
    // KO(결투장 측정: 결정타 뒤 화면 약 -40%·옅은 붉은 기운): 밝기를 약 41% 낮추고 붉은 쪽으로 조금 기울인 뒤, 가장자리만 짙은 진홍으로 감싼다.
    // 2026-10-14 실화면: (0.62,0.40,0.42)·밝은 진홍 테두리는 화면 전체를 분홍으로 덮어 피격·각성 화면과 구별되지 않았다
    c = mix(c, c.mul(vec3(0.68, 0.56, 0.56)), this.ko);
    c = mix(c, vec3(0.3, 0.0, 0.03), smoothstep(0.62, 1.0, rad).mul(this.ko).mul(0.6));
    // 집중선: 각도 구간마다 폭이 다른 정적 방사선(시간에 따라 흔들리지 않는다)
    const n = atan(dir.y, dir.x).mul(72 / (Math.PI * 2)).add(36);
    const id = n.floor();
    const f = n.fract().sub(0.5).abs();
    const w = hash(id).mul(0.2).add(0.03);
    const line = smoothstep(w.mul(0.25), w, f).oneMinus().mul(step(0.45, hash(id.add(17))));
    const mask = smoothstep(0.2, 0.7, rad);
    c = mix(c, vec3(1, 0.94, 0.94), line.mul(mask).mul(this.lines.add(this.koLines)).mul(0.7));
    // 섬광(톤매핑 뒤 → 순백 가능). 세기와 색은 FlashLimiter와 설정(섬광 약화)이 정한다.
    c = mix(c, this.flashColor, this.flash);

    this.pipeline = new THREE.RenderPipeline(renderer);
    this.pipeline.outputColorTransform = false;
    this.pipeline.outputNode = vec4(c, 1);
  }

  /** 맵 밝기에 맞춘 블룸(마을은 밝아서 임계를 높인다) */
  setBloom(strength: number, threshold: number): void {
    this.bloomBase = strength;
    this.bloomNode.strength.value = strength * this.bloomMul;
    this.bloomNode.threshold.value = threshold;
  }

  /** 연출용 블룸 배율(0..1). 맵 기본 세기에 곱한다 */
  setBloomScale(k: number): void {
    if (k === this.bloomMul) return;
    this.bloomMul = k;
    this.bloomNode.strength.value = this.bloomBase * k;
  }

  /** 맵 분위기 적용: 블룸 + 햇무리 + 채도 */
  setMood(m: MapMood): void {
    this.setBloom(m.bloom, m.threshold);
    this.glowColor.value.setHex(m.glow);
    this.glow.value = m.glowI;
    this.glowAt.value.set(m.glowX, m.glowY);
    this.saturation.value = m.sat;
    // 톤매핑 뒤(화면 sRGB 값)에 곱하므로 16진 색을 sRGB 값 그대로 쓴다
    _c.setHex(m.shadowTint).convertLinearToSRGB();
    this.shadowTint.value.set(_c.r, _c.g, _c.b);
    _c.setHex(m.highTint).convertLinearToSRGB();
    this.highTint.value.set(_c.r, _c.g, _c.b);
    this.split.value = m.split;
    this.lift.value = m.lift;
  }

  /**
   * 실행 중 품질 변경(자동 품질 조절). 그래프 구조는 그대로 두고 장면 패스 표본 수와 블룸 해상도만 바꾼다.
   * 표본 수가 바뀌면 렌더 타깃 텍스처와 파이프라인이 한 번 다시 만들어진다(한 프레임 끊김).
   */
  setQuality(msaa: boolean, bloomScale: number): void {
    const s = msaa ? 4 : 0;
    if (s !== this.samples) {
      this.samples = s;
      (this.scenePass as unknown as { options: { samples?: number } }).options.samples = s;
      this.scenePass.renderTarget.samples = s;
    }
    if (bloomScale !== this.bloomScale) {
      this.bloomScale = bloomScale;
      this.bloomNode.setResolutionScale(0.5 * bloomScale);
    }
  }

  /**
   * 장면이 실제로 그려지는 렌더 타깃(MSAA 표본 수·깊이 스텐실 형식). compileAsync는 그 순간의 렌더 타깃으로 파이프라인 캐시 키를 만들므로,
   * 미리 컴파일은 이 타깃을 걸고 해야 실제 그리기에서 재사용된다(캔버스로 컴파일하면 첫 그리기에서 동기 파이프라인이 다시 생긴다).
   */
  get sceneTarget(): THREE.RenderTarget {
    return this.scenePass.renderTarget;
  }

  render(): void {
    this.pipeline.render();
  }

  dispose(): void {
    this.pipeline.dispose();
  }
}
